// <emma-chat> — root Custom Element. Public API for host pages.
//
// Usage:
//   <script type="module" src="/widget/dist/emma-chat.js"></script>
//   <emma-chat
//     api-base=""              <!-- BFF URL prefix, '' = same-origin -->
//     auth-token="..."         <!-- optional Bearer token -->
//     session-id="..."         <!-- optional session correlation id -->
//   ></emma-chat>
//
// What this element owns:
//   - The single ChatStateStore for the widget. Provided through Lit
//     Context to all descendants (T04).
//   - Service clients (SSE, API, Capabilities) wired with the same
//     auth-token + api-base.
//   - Capability fetch on connect (with anonymous fallback on AuthError).
//   - Composition: <emma-chat-input> on top, <emma-chat-message-list>
//     below — message-list owns the bottom CTA per DS rule.
//   - Cross-component event handlers: input-submit / refine-submit /
//     chip-click / refine-trigger-click / card-click / deploy-click.
//   - SSE pump → store mutators. Fires window.dispatchEvent for the
//     scanning-signal so message-list's placeholder timing works.
//
// What this element does NOT own:
//   - Visual styling of bubbles / cards / inputs — those are in the leaf
//     components (T06–T11). Root only sets host display + a thin layout.
//   - Pick → deploy ID transfer logic (post-launch task; lastPickedIDs
//     is stored but not threaded into the next request body until then).

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ContextProvider } from '@lit/context';

import { designTokens } from './components/design-tokens.js';
import './components/emma-chat-input.js';
import './components/emma-chat-message-list.js';
// Decorative dot-grid is opt-in for hosts (mounted as a sibling, not a child
// of <emma-chat>). Side-effect import ensures the custom element is defined
// once the widget bundle loads, so landings can `<emma-chat-dot-grid>` without
// a second script tag.
import './components/emma-chat-dot-grid.js';

import {
  ChatStateStore,
  boundDocCard,
  boundSystemEvent,
  boundSystemEventAction,
  chatStateContext,
  DOC_CARD_TEXT_MAX,
  SYSTEM_EVENT_TEXT_MAX,
  type DocCardEntry,
  type TurnEntry,
} from './state/index.js';
import { SSEClient } from './services/sse-client.js';
import { ApiClient } from './services/api-client.js';
import {
  boundTranscript,
  findSelectedCardId,
  threadTranscriptKey,
} from './services/transcript-carry.js';
import { CapabilitiesClient } from './services/capabilities-client.js';
import { AuthError, NetworkError, RateLimitError } from './services/errors.js';
import type {
  SearchRequest,
  SSEData,
  Provider,
  CompactCard,
} from './types/index.js';

@customElement('emma-chat')
export class EmmaChat extends LitElement {
  /** BFF URL prefix. Empty (default) = same-origin. */
  @property({ type: String, attribute: 'api-base' })
  apiBase = '';

  /** Bearer token. When set, capabilities + SSE include Authorization. */
  @property({ type: String, attribute: 'auth-token' })
  authToken = '';

  /** Session correlation id passed in SearchRequest + ActionRequest.
   *  Auto-generated on first connect when the host doesn't supply one
   *  (see `connectedCallback`). Stable for the widget's lifetime — same
   *  session id on every request, every refine, every action. The host
   *  can override at any time by setting the attribute / property; the
   *  new id becomes the source of truth on the next request. */
  @property({ type: String, attribute: 'session-id' })
  sessionId = '';

  /** Embed surface. "portal" unlocks RAG doc-grounding (answers grounded in
   *  docs.emma.ms with citations); default/"hero" funnels how-to questions to
   *  the portal CTA instead. The portal embed sets surface="portal". */
  @property({ type: String, attribute: 'surface' })
  surface = '';

  /** Docsie article id of the page the user is on (portal passes the route's
   *  docsieArticleId). Sent as contextArticleId → rag-service boosts that
   *  article + siblings at retrieval (section-awareness). Empty = no boost. */
  @property({ type: String, attribute: 'context-article-id' })
  contextArticleId = '';

  /** Which doc corpus grounds the answers: 'docsie' or 'docs-site' (the
   *  self-hosted replacement). EMPTY MEANS "let the contour decide" — the field is
   *  then omitted from the request and the BFF applies DOCS_SOURCE_DEFAULT, which
   *  is docs-site since the 2026-08 flip. That indirection is deliberate: a page
   *  embedding a cached copy of this bundle must follow the contour without being
   *  rebuilt. Set it explicitly only to PIN a corpus — the portal does, from a
   *  feature flag, so one flag moves a user wholesale: the doc card they read and
   *  the answer their chip gets come from one source. */
  @property({ type: String, attribute: 'docs-source' })
  docsSource = '';

  /** Page slug for the section boost on the docs-site corpus — its equivalent of
   *  contextArticleId (a quarter of its pages have no Docsie ancestor, so the
   *  art_* id cannot key it). Both travel; the BFF uses the one matching the
   *  selected source. */
  @property({ type: String, attribute: 'context-doc-slug' })
  contextDocSlug = '';

  /** Opaque page context the portal embed attaches (T1: routeName/section/tag;
   *  later entity/permission). Forwarded to the BFF AS-IS — the widget never
   *  inspects its fields, so new fields need no widget change or bundle rebuild.
   *  Property-only (no attribute): the portal sets it via `.portalContext` /
   *  `:portal-context.prop`. The BFF whitelists keys + bounds values. */
  @property({ attribute: false })
  portalContext: Record<string, unknown> | null = null;

  /** Soft cap on refines per session — once turnHistory exceeds this,
   *  the refine UI is hidden and the bottom CTA becomes the primary
   *  action. Mirrors monolith MAX_REFINES (index.html :2476). Configurable
   *  via attribute so the host page can tighten/loosen the gate (default
   *  5; product plans to drop to 3). */
  @property({ type: Number, attribute: 'max-refines' })
  maxRefines = 5;

  /** When true, the bottom-CTA "Continue with emma" click does NOT open
   *  a new tab on its own — the widget only dispatches `emma-deploy-click`
   *  and lets the host page take over. Use case: portal embed wants to
   *  keep the user inside the SPA flow instead of redirecting to the
   *  signup URL. Host listens for the event on the widget element and
   *  routes the user wherever it likes:
   *
   *    widget.externalDeployHandler = true;
   *    widget.addEventListener('emma-deploy-click', (e) => {
   *      portal.openDeployFlow(e.detail);
   *    });
   *
   *  Without this attribute the widget keeps its standalone behaviour
   *  (popup-blocker-safe sync window.open + signup-URL redirect). */
  @property({ type: Boolean, attribute: 'external-deploy-handler' })
  externalDeployHandler = false;

  /** Compact / floating embed mode (the portal chat-launcher panel). The host
   *  is given a fixed height by its container, so the conversation scrolls
   *  WITHIN the card instead of growing the document; the standalone card
   *  chrome (shadow/border/radius) is dropped because the panel provides its
   *  own; and the per-turn auto-scroll targets the internal scroller so it
   *  never yanks the whole page. Off by default — inline/full-page embeds are
   *  unaffected.
   *
   *  `reflect: true` is REQUIRED: frameworks (e.g. Vue 3) set a matching bare
   *  attribute as a DOM *property* on the upgraded element rather than an
   *  attribute, which would leave the `:host([compact])` CSS un-matched.
   *  Reflecting the property back to the `compact` attribute makes the host
   *  styling fire regardless of how a consumer sets it (attribute or property). */
  @property({ type: Boolean, attribute: 'compact', reflect: true })
  compact = false;

  /** Cycling thinking message rendered inside <emma-chat-input>'s overlay
   *  during initial submit. Mirrors monolith textarea overlay (index.html
   *  :2960-2964 — cycles `thinkingMessages[scenario][i]` every 700ms). */
  @state() private thinkingMessage = '';

  /** True for the brief 200ms during which the conversation fades out
   *  before the store reset switches the template back to <emma-chat-input>.
   *  Drives the .chat-frame.is-resetting opacity transition. */
  @state() private resetting = false;

  /** True between `startWithConfig` entry and either the synthetic
   *  turn being pushed (success) or an error path completing.
   *  Drives a "loading" state so the user doesn't see the unrelated
   *  demo typewriter while waiting for /api/hero/config to return. */
  @state() private carryDeployLoading = false;

  /** AbortController for the current SSE pump. Cancelled on unmount,
   *  on Stop / Start over (abortInFlight), or on a new submit before
   *  the prior completes. */
  private currentAbort: AbortController | null = null;
  /** Monotonic run epoch. Bumped whenever an in-flight run is torn down
   *  (Stop / Start over / re-submit takeover). runSearch captures it at
   *  start and refuses to finalize when it has moved — this closes the
   *  finalize-after-teardown race: once the SSE promise has already
   *  resolved, abort() is a no-op, so without the epoch check
   *  finalizeTurn would still push the answer into a stopped (or about
   *  to be reset) conversation. */
  private runEpoch = 0;
  private thinkingCycleTimer: number | null = null;
  /** Compose-phase cycle: once the server says "Composing…", the BFF is blocked
   *  on a single (10-20s) grounding LLM call with no further SSE, so the widget
   *  cycles its own progress copy to keep the indicator alive (E). */
  private composeCycleTimer: number | null = null;

  // --- Store + Context provider ---
  private store = new ChatStateStore();

  constructor() {
    super();
    // ContextProvider registers itself with this host on construction
    // (like StateConsumerController). We don't retain the reference —
    // Lit's controller registry keeps it alive.
    new ContextProvider(this, {
      context: chatStateContext,
      initialValue: this.store,
    });
  }

  // --- Service clients ---
  private sseClient = new SSEClient();
  private apiClient = new ApiClient();
  private capabilitiesClient = new CapabilitiesClient();

  static override styles = [
    designTokens,
    css`
      :host {
        display: block;
        font-family: var(--font-sans);
        color: var(--color-text);
      }
      /* Single white card wraps the conversation. Mirrors monolith
         .emma-hero__prompt-card (index.html :201-212): same 14px radius,
         soft 2-layer shadow, gray-10 border. The DS .chat-frame in
         search-results.html omits the shadow but the production hero
         has it — going with the production look for the swap PR. */
      .chat-frame {
        background: var(--white);
        border: 1px solid var(--gray-10);
        border-radius: 14px;
        box-shadow:
          0 4px 24px hsla(225, 20%, 60%, 0.12),
          0 0 48px hsla(220, 15%, 85%, 0.3);
        padding: var(--space-4);
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
        transition: opacity 200ms var(--easing);
      }
      .chat-frame.is-resetting {
        opacity: 0;
        pointer-events: none;
      }

      /* Compact / floating embed (portal chat launcher): the widget IS the
         visible card — its own .chat-frame chrome (radius/shadow/border) is
         KEPT, so the launcher panel can be a transparent positioner with no
         wrapper box around it (no double-box). The card grows with content
         from its natural size up to a host-provided cap
         (--emma-chat-max-height, set by the launcher to "FAB → top margin"),
         then scrolls INSIDE the card. Scrollbar is thin, borderless, and only
         appears on hover. */
      :host([compact]) .chat-frame {
        /* border-box so max-height includes the card's padding + border —
           otherwise the panel ends up ~34px taller than the cap and clips
           above the viewport top. */
        box-sizing: border-box;
        max-height: var(--emma-chat-max-height, 80vh);
        overflow-y: auto;
        scrollbar-width: thin; /* Firefox */
        scrollbar-color: transparent transparent;
      }
      :host([compact]) .chat-frame:hover {
        scrollbar-color: var(--gray-20, #d8dee9) transparent;
      }
      :host([compact]) .chat-frame::-webkit-scrollbar {
        width: 6px;
      }
      :host([compact]) .chat-frame::-webkit-scrollbar-track {
        background: transparent;
      }
      :host([compact]) .chat-frame::-webkit-scrollbar-thumb {
        background: transparent;
        border-radius: 3px;
      }
      :host([compact]) .chat-frame:hover::-webkit-scrollbar-thumb {
        background: var(--gray-20, #d8dee9);
      }

      /* Arrival animation when <emma-chat-input> mounts (initial page
         load AND on conversation reset — Lit re-mounts the element,
         so the keyframe runs each time). */
      emma-chat-input {
        animation: emma-input-arrive 280ms cubic-bezier(0, 0, 0.2, 1) both;
      }
      @keyframes emma-input-arrive {
        from {
          opacity: 0;
          transform: translateY(-6px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .chat-frame {
          transition: none;
        }
        emma-chat-input {
          animation: none;
        }
      }

      /* Carry-deploy loading view — minimal, fills the chat-frame
         while /api/hero/config returns. Mirrors the monolith scanning
         visual language (dots + faint text) without pulling in
         emma-chat-result-header, which lives downstream behind the
         message-list provide('creature') context the root doesn't set. */
      .carry-loading {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 12px;
        padding: 48px 24px;
        color: var(--gray-60, hsl(222, 12%, 42%));
        font-size: var(--text-sm, 0.875rem);
      }
      .carry-loading__dots {
        display: inline-flex;
        gap: 6px;
      }
      .carry-loading__dots span {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: var(--gray-40, hsl(222, 10%, 66%));
        animation: carry-loading-pulse 1.2s ease-in-out infinite both;
      }
      .carry-loading__dots span:nth-child(2) {
        animation-delay: 0.15s;
      }
      .carry-loading__dots span:nth-child(3) {
        animation-delay: 0.3s;
      }
      @keyframes carry-loading-pulse {
        0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
        40% { opacity: 1; transform: scale(1); }
      }
      @media (prefers-reduced-motion: reduce) {
        .carry-loading__dots span {
          animation: none;
          opacity: 0.6;
        }
      }
    `,
  ];

  /** Unsubscribe handle returned from store.subscribe — invoked on
   *  disconnect so we don't leak across hot-reloads / DOM moves. */
  private storeUnsubscribe: (() => void) | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    // Auto-generate session-id when host didn't supply one. Cross-request
    // correlation in BFF analytics works regardless of who minted the
    // id — but a stable id per widget instance lets the BFF group
    // turns from one conversation. crypto.randomUUID is available in
    // every modern browser; the time+random fallback is for unusual
    // environments (older Safari, jsdom in tests).
    if (!this.sessionId) {
      this.sessionId = generateSessionId();
    }
    // Subscribe root to store mutations so any pushTurn / mode-flip
    // re-renders the top-level template (and re-evaluates `hasTurns`,
    // which gates input vs message-list rendering). Children consume
    // via ContextProvider/StateConsumerController already, but the
    // root itself needs its own subscription — ContextProvider doesn't
    // re-render its host on context-value changes. Pre-Phase-1 the
    // existing search flow accidentally worked because
    // `startThinkingCycle` mutates a root @state every ~800ms, so the
    // root always re-rendered before turnHistory was actually checked.
    // Carry-deploy (startWithConfig / startWithNavigation) doesn't run
    // the thinking cycle → without this subscription the root stays
    // stuck in input-mode even after pushTurn lands the synthetic turn.
    this.storeUnsubscribe = this.store.subscribe(() => this.requestUpdate());
    this.applyServiceConfig();
    void this.loadCapabilities();
    void this.loadProviders();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.currentAbort) {
      this.currentAbort.abort();
      this.currentAbort = null;
    }
    this.stopThinkingCycle();
    if (this.storeUnsubscribe) {
      this.storeUnsubscribe();
      this.storeUnsubscribe = null;
    }
  }

  override updated(changed: Map<string, unknown>): void {
    if (changed.has('apiBase') || changed.has('authToken')) {
      this.applyServiceConfig();
    }
  }

  /** Re-init service clients with current attribute values. Cheap (no
   *  fetch) — services hold only config, no connections. */
  private applyServiceConfig(): void {
    this.sseClient = new SSEClient({
      apiBase: this.apiBase,
      authToken: this.authToken || undefined,
    });
    this.apiClient = new ApiClient({
      apiBase: this.apiBase,
      authToken: this.authToken || undefined,
    });
    this.capabilitiesClient = new CapabilitiesClient({
      apiBase: this.apiBase,
      authToken: this.authToken || undefined,
    });
  }

  private async loadCapabilities(): Promise<void> {
    // Anonymous-mode short-circuit. Without an auth-token the widget is
    // running in landing context — feature flags can't be user-specific
    // there, so spending a round-trip on /api/hero/capabilities is just
    // latency. Use the canonical anonymous defaults inline. The portal
    // embed always passes auth-token, which falls through to the real
    // fetch below where the BFF can return user-specific capabilities.
    if (!this.authToken) {
      this.store.setCapabilities(CapabilitiesClient.anonymousFallback());
      return;
    }
    try {
      const caps = await this.capabilitiesClient.fetch();
      this.store.setCapabilities(caps);
    } catch (e) {
      // AuthError or NetworkError — fall back to anonymous so the widget
      // is usable even when /api/hero/capabilities is unreachable.
      if (e instanceof AuthError || e instanceof NetworkError) {
        this.store.setCapabilities(CapabilitiesClient.anonymousFallback());
      } else {
        throw e;
      }
    }
  }

  private async loadProviders(): Promise<void> {
    try {
      // ProvidersResponse is Provider[] directly (no wrapper field).
      this.providers = await this.apiClient.getProviders();
    } catch {
      // Provider list is decorative. Silently fall back to empty —
      // <emma-chat-input> already handles an empty providers prop.
      this.providers = [];
    }
  }

  /** Provider list passed down to <emma-chat-input>. State because the
   *  fetch is async and we want to re-render once it lands. */
  @state() private providers: Provider[] = [];

  // ---------------- Cross-component event handlers ----------------

  private handleInputSubmit = (e: CustomEvent<{ text: string; selectedProviders: string[] }>): void => {
    const text = e.detail.text.trim();
    if (!text) return;
    void this.runSearch(text, { providers: e.detail.selectedProviders });
  };

  private handleRefineSubmit = (e: CustomEvent<{ text: string }>): void => {
    const text = e.detail.text.trim();
    if (!text) return;
    // Any trusted refine clears carry-deploy mode — subsequent turns
    // should use standard timing for the bottom CTA reveal.
    if (this.store.state.carryDeployMode !== null) {
      this.store.setCarryDeployMode(null);
    }
    // Refine only after a search turn; a follow-up typed under a doc answer runs
    // fresh so it isn't carried into a price refine.
    void this.runSearch(text, { isRefine: this.lastTurnWasSearch() });
  };

  /** Carry-deploy navigate-mode "Refine here" button — soft transition out
   *  of the navigate prompt back to the standard refine flow. Clears the
   *  carry-deploy mode flag so subsequent UX (bottom-CTA timing, etc.) is
   *  back to normal; focuses the refine input so the user can type
   *  immediately. The synthetic navigate-prompt turn stays in
   *  turnHistory (no visual jump) — the next user submit just appends. */
  private handleCarryRefineHere = (): void => {
    this.store.setCarryDeployMode(null);
    // Focus the refine input on the next frame so reactive re-renders
    // (mode flag flipping) finish layout first.
    requestAnimationFrame(() => {
      const list = this.renderRoot?.querySelector('emma-chat-message-list');
      const refine = list?.shadowRoot?.querySelector('emma-chat-refine');
      const textarea = refine?.shadowRoot?.querySelector('textarea');
      (textarea as HTMLTextAreaElement | null)?.focus();
    });
  };

  /** A query is a "refine" only when the PREVIOUS turn produced a search result.
   *  After a consultative (doc) turn there are no search params to refine — a
   *  follow-up chip / input must be a FRESH query, else it carries the last
   *  search's params (lastResolvedParams) and a doc question gets misrouted into a
   *  price refine (e.g. a K8s how-to landing on a spot VM). */
  private lastTurnWasSearch(): boolean {
    const h = this.store.state.turnHistory;
    return !!h[h.length - 1]?.sseData?.result;
  }

  /** Quick-chip OR oob-suggestion click — both emit emma-chip-click with
   *  {label, prompt}. Refine only if the previous turn was a search (see
   *  lastTurnWasSearch); doc-topic chips from a consultative turn run fresh. */
  private handleChipClick = (
    e: CustomEvent<{ label: string; prompt: string; origin?: string }>,
  ): void => {
    const text = e.detail.prompt?.trim();
    if (!text) return;
    if (this.store.state.carryDeployMode !== null) {
      this.store.setCarryDeployMode(null);
    }
    // Help/Docsie follow-up chips (origin='help', emitted by the consultative
    // bubble) want the help ARTICLE, not a config search — a bare topic label
    // like "Spot instance - Monitoring" otherwise trips the search classifier.
    // Tag the submit so the BFF routes it to the grounding/RAG lane (QA #10).
    const helpSource = e.detail.origin === 'help';
    // Echo the chip as a visible user message immediately. A typed query is
    // already visible (it sits in the input it was typed into); a chip has no
    // input, so without this the question vanishes and only a spinner shows.
    // optimisticEcho pushes a pending user-turn that finalizeTurn swaps for the
    // real response.
    void this.runSearch(text, {
      isRefine: this.lastTurnWasSearch(),
      optimisticEcho: true,
      helpSource,
    });
  };

  /** Card click — record selection in the store (single global card across
   *  all turns). The selected card's ctaUrl will be used when the user
   *  clicks the bottom CTA. Clicking the same card twice toggles selection
   *  off (revert to per-turn best-price highlight). */
  private handleCardClick = (e: CustomEvent<{ cardId: string }>): void => {
    const id = e.detail.cardId;
    if (!id) return;
    const current = this.store.state.selectedCardId;
    this.store.setSelectedCardId(current === id ? null : id);
  };

  /** Fired by <emma-chat-turn> 100ms after a fresh result-bearing turn
   *  starts its reveal timeline. Scroll the chat-frame (white card)
   *  into view so the user sees the conversation from the top —
   *  matches monolith :4234 + :3523 scrollIntoView pattern (which
   *  scrolls the entire response area, not just the new turn). */
  private handleTurnRevealed = (e: Event): void => {
    const frame = (e.currentTarget as HTMLElement) ?? null;
    if (!frame) return;
    if (this.compact) {
      // Floating-panel mode: scroll WITHIN the internal .chat-frame scroller,
      // never via scrollIntoView — with the page itself scrolled (no inner
      // scroll container in standalone mode) scrollIntoView yanks the whole
      // document out from under the launcher. Here the .chat-frame is the
      // overflow-y:auto scroller, so align the new turn to its top.
      const scroller = this.renderRoot?.querySelector(
        '.chat-frame',
      ) as HTMLElement | null;
      if (scroller) {
        const top =
          frame.getBoundingClientRect().top -
          scroller.getBoundingClientRect().top +
          scroller.scrollTop;
        scroller.scrollTo({ top, behavior: 'smooth' });
      }
      return;
    }
    frame.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /** Deploy CTA at the bottom of the conversation. Posts to /api/hero/action
   *  to get a pre-filled signup URL, then opens it.
   *
   *  CRITICAL: Safari + Firefox block `window.open()` from async callbacks
   *  (after fetch resolves). To avoid that, open a placeholder tab
   *  SYNCHRONOUSLY in the click handler, then redirect it via
   *  `win.location.href` once the action POST resolves. Mirrors monolith
   *  index.html :4449-4483. We do NOT pass 'noopener' — per MDN that
   *  nulls the returned reference, breaking the deferred-href pattern. */
  private handleDeployClick = (e: CustomEvent<{ url: string }>): void => {
    if (typeof window === 'undefined') return;

    // Product metric: count EVERY bottom-CTA click. This MUST fire before any
    // early return / navigation below, because the three click paths otherwise
    // leave the BFF blind — the portal-embed path returns immediately, and the
    // dominant fast path opens a pre-built URL client-side without a POST. The
    // beacon (sendBeacon → /api/hero/event) is the single choke point that sees
    // them all. `surface` mirrors the button 1:1: externalDeployHandler renders
    // "Deploy to project" inside the portal, otherwise it's "Continue with emma"
    // on the landing. The bottom CTA is always a deploy intent.
    this.apiClient.sendCtaClick(
      this.externalDeployHandler ? 'portal' : 'hero',
      'deploy',
      // Correlate the click to the conversation (Phase 1a per-session journey).
      // Same pair the dialog-carry POST below sends — sessionId joins to the
      // BFF's query_analytics lines; turnIndex marks which turn the click came
      // from (the bottom CTA only renders after a result, so this is >= 0).
      this.sessionId || undefined,
      this.store.state.turnHistory.length - 1,
    );

    // Host-take-over mode: portal embed sets `external-deploy-handler`
    // when it wants to handle the deploy click in its own SPA flow
    // (e.g. open an internal route, log analytics, gate by entitlement).
    // The widget bubbles the event up via composed:true; portal listens
    // for `emma-deploy-click` on the <emma-chat> element and reads
    // detail. We skip the default window.open / postAction here.
    if (this.externalDeployHandler) return;

    // Dialog-carry (Phase 1): stash the verbatim conversation BEFORE either
    // deploy branch runs. The dominant standalone gesture (clicking a result /
    // deploy_intent CTA) always carries an explicit URL and takes the fast path
    // below — so the capture MUST live here, not inside the postAction branch,
    // or it would never fire for the primary path. Mints a fresh nonce + fires
    // a keepalive POST (survives this navigation). Best-effort — never blocks or
    // breaks the deploy nav.
    const transcriptKey = this.captureTranscript();

    // Fast path: deploy_intent / result card already carried a complete signup
    // URL. Thread the nonce INTO its redirect target (Keycloak preserves only
    // the redirect target's own query, so a top-level param would be dropped),
    // then open synchronously — no async POST needed.
    const explicitUrl = e.detail.url;
    if (explicitUrl) {
      window.open(threadTranscriptKey(explicitUrl, transcriptKey), '_blank');
      return;
    }

    // Slow path (no pre-built URL, e.g. a pick-only turn): open the placeholder
    // tab synchronously while the click is fresh, then POST to /api/hero/action,
    // which threads the nonce into the signup target server-side.
    const win = window.open('', '_blank');

    void this.apiClient
      .postAction({
        action: 'deploy',
        config: this.store.state.lastResolvedParams ?? undefined,
        sessionId: this.sessionId || undefined,
        turnIndex: this.store.state.turnHistory.length - 1,
        transcriptKey,
      })
      .then((res) => {
        const url = res.signupUrl;
        if (win) win.location.href = url;
        else window.location.href = url;
      })
      .catch(() => {
        // Action failed — close the placeholder tab so user isn't left
        // with an empty page (host can still wire a fallback signup link).
        if (win) win.close();
      });
  };

  /** Stash the verbatim turnHistory on the BFF keyed by a fresh single-use
   *  nonce; returns the nonce to thread into the signup target (dialog-carry
   *  Phase 1), or undefined if there's nothing to carry / crypto is absent.
   *  Best-effort: the keepalive POST is fire-and-forget and never throws into
   *  the deploy path. */
  private captureTranscript(): string | undefined {
    try {
      const all = this.store.state.turnHistory;
      if (!all.length) return undefined;
      const nonce =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : '';
      if (!nonce) return undefined;
      // Bound the payload to the keepalive transport BEFORE sending (the browser
      // drops an oversized keepalive body before it leaves, so the BFF's
      // post-decode caps can't save it). Skip the stash if even one turn is too
      // big — then no nonce is threaded and the portal simply finds nothing.
      const turns = boundTranscript(all, nonce);
      if (!turns.length) return undefined;
      void this.apiClient.stashTranscript(nonce, turns).catch(() => {});
      return nonce;
    } catch {
      return undefined;
    }
  }

  // ---------------- Conversation reset ----------------

  /** Reset the conversation back to the initial input screen.
   *  Triggered by the OOB "Start over" chip in <emma-chat-turn>, which
   *  dispatches `emma-clear-conversation`; the chat-frame catches it
   *  and calls `clearConversation` here. The 200ms `resetting` window
   *  plays the chat-frame fade-out via CSS transition; on completion
   *  `store.reset()` flips turnHistory to empty so the template
   *  re-renders to <emma-chat-input>, which then runs its own arrival
   *  animation. store.reset() preserves capabilities and clears every
   *  other state field including selectedCardId. */
  private clearConversation(): void {
    // Tear down any in-flight run FIRST — before the guards below. During
    // the FIRST turn a typed query pushes no optimistic turn, so
    // turnHistory is still empty; the empty-history guard used to return
    // before the abort and the in-flight SSE would still deliver its
    // answer (QA #5). The guards now only skip the fade-out animation.
    this.abortInFlight();
    if (this.resetting) return;
    if (this.store.state.turnHistory.length === 0) return;
    this.resetting = true;
    window.setTimeout(() => {
      this.store.reset();
      this.resetting = false;
    }, 200);
  }

  /** Shared in-flight teardown for Stop / Start over: cancel the SSE
   *  pump, stop the thinking cycle, drop the optimistic pending turn
   *  (chip echo) and clear isSubmitting. Bumping runEpoch invalidates a
   *  run whose stream already resolved (abort() no-ops then), so its
   *  finalizeTurn can't push the answer into a stopped conversation.
   *  Completed turns are untouched — Stop ≠ Start over. */
  private abortInFlight(): void {
    this.runEpoch++;
    if (this.currentAbort) {
      this.currentAbort.abort();
      this.currentAbort = null;
    }
    this.stopThinkingCycle();
    this.store.dropTrailingPendingTurn();
    this.store.setSubmitting(false);
  }

  /** Listener for emma-clear-conversation event bubbled from any
   *  descendant (currently the OOB "Start over" chip in <emma-chat-turn>). */
  private handleClearConversation = (): void => {
    this.clearConversation();
  };

  /** Listener for emma-stop-thinking bubbled from any thinking surface
   *  (input overlay / refine scanning row / synthetic loader / pending
   *  bubble). Cancels the in-flight run but keeps the conversation and
   *  the user's typed query. */
  private handleStopThinking = (): void => {
    this.abortInFlight();
  };

  // ---------------- Search pipeline ----------------

  /** Submit a search query (initial or refine) and pump the SSE stream
   *  into the store. Manages isSubmitting + abort across
   *  re-submits. */
  private async runSearch(
    text: string,
    opts: {
      providers?: string[];
      isRefine?: boolean;
      optimisticEcho?: boolean;
      helpSource?: boolean;
    } = {},
  ): Promise<void> {
    if (this.store.state.isSubmitting) {
      // Cancel the in-flight stream first so the new query takes over.
      // Bump the epoch too: if the old stream has ALREADY resolved, its
      // abort is a no-op and only the epoch check stops its stale
      // finalizeTurn from landing next to this run's.
      this.runEpoch++;
      if (this.currentAbort) this.currentAbort.abort();
    }
    // Drop any optimistic pending turn orphaned by a just-aborted run so rapid
    // re-submits don't stack pending bubbles (and so buildRequest's turnIndex /
    // prior-cards see a clean history).
    this.store.dropTrailingPendingTurn();

    const abort = new AbortController();
    this.currentAbort = abort;
    // Captured AFTER the takeover bump above — a later abortInFlight (Stop /
    // Start over) moves runEpoch past this and invalidates the run.
    const epoch = this.runEpoch;

    this.store.setSubmitting(true);
    this.store.setSubmitTimestamp(Date.now());

    // Cycle thinking-overlay messages on every submit (initial AND
    // refine). Mirrors monolith index.html :2960-2964. Consumers:
    //   - <emma-chat-input> overlay (only visible on first turn)
    //   - refine-input's .refine-scanning indicator (active refine
    //     in result-state turn)
    //   - <emma-chat-message-list>'s synthetic loader bubble (refine
    //     from OOB / deploy / pick / consultative chip — no in-bubble
    //     indicator otherwise)
    // Earlier we gated this on `!opts.isRefine` because the only refine
    // surface (the in-bubble refine-input) had its own static "Searching…"
    // default. With the synthetic loader added, that default no longer
    // covers every case, and starting the cycle universally just gives
    // every consumer a live scenario-aware message for free.
    this.startThinkingCycle(text);

    // buildRequest BEFORE the optimistic push so turnIndex / prior-cards reflect
    // the real history (the pending turn carries no result and must not count).
    const req: SearchRequest = this.buildRequest(text, opts);

    // Optimistic echo: show the user's question (a clicked chip) immediately as
    // a pending user-turn. finalizeTurn replaces it with the real response.
    if (opts.optimisticEcho) {
      this.store.pushTurn({ query: text, sseData: {}, resolvedParams: null, pending: true });
    }

    let sseData: SSEData;
    try {
      sseData = await this.sseClient.search(
        req,
        {
          onThinkingProgress: (e) => {
            // Server-sent thinking_progress overrides the local cycling
            // basket (mirror monolith :3114-3115). Once the server starts
            // talking, stop the local interval so the basket doesn't
            // overwrite the server message at the next 700ms tick. The
            // refine flow consumes the same channel via the store-driven
            // indicator inside <emma-chat-refine>.
            if (!e?.message) return;
            this.stopThinkingCycle(/* keepText */ true);
            // The grounding "Composing…" beat is the last SSE before a long
            // (10-20s) single LLM call — pinning one static line reads as
            // frozen. Hand off to a local compose cycle that keeps the
            // indicator alive until the result lands (E). Other beats (e.g.
            // "Searching the docs…") just pin as before.
            if (isComposingMessage(e.message)) {
              this.startComposeCycle();
            } else {
              this.thinkingMessage = e.message;
            }
          },
        },
        abort.signal,
      );
    } catch (e) {
      // AbortError → user stopped or replaced the request, don't push a turn.
      if (e instanceof Error && e.name === 'AbortError') {
        // abortInFlight and the re-submit takeover both clear/replace
        // currentAbort AND own the isSubmitting state themselves. If the
        // aborted controller is somehow still the current one (an abort
        // outside those paths), clear the stuck 'thinking' state here —
        // but never when a NEWLY started submit has already replaced the
        // controller, so we don't clobber the new run's isSubmitting.
        if (this.currentAbort === abort) {
          this.currentAbort = null;
          this.stopThinkingCycle();
          this.store.dropTrailingPendingTurn();
          this.store.setSubmitting(false);
        }
        return;
      }
      // Synthesize an oob turn so the conversation doesn't dead-end.
      const message =
        e instanceof RateLimitError
          ? e.message
          : e instanceof NetworkError
            ? 'Network error — try again in a moment.'
            : 'Something went wrong. Try rephrasing.';
      sseData = { oob: { message, suggestions: [] } };
    } finally {
      // Only clear if THIS run is still the active one (a faster re-submit
      // may have replaced currentAbort).
      if (this.currentAbort === abort) {
        this.currentAbort = null;
      }
    }

    if (epoch !== this.runEpoch) {
      // A Stop / Start over (or a takeover) tore this run down after the
      // stream had already resolved — abort() was a no-op by then. The
      // teardown already restored the store; finalizing would push the
      // answer into the stopped/cleared conversation.
      return;
    }
    this.finalizeTurn(text, sseData);
  }

  private buildRequest(
    text: string,
    opts: { providers?: string[]; isRefine?: boolean; helpSource?: boolean },
  ): SearchRequest {
    const s = this.store.state;
    const turnIndex = s.turnHistory.length;
    // Match monolith bodyObj (index.html :3015-3021) — currency:'EUR'
    // always sent, providers omitted when empty (vs explicit undefined).
    const req: SearchRequest = {
      query: text,
      currency: 'EUR',
      turnIndex,
      sessionId: this.sessionId || undefined,
    };
    if (this.surface) {
      req.surface = this.surface;
    }
    if (this.contextArticleId) {
      req.contextArticleId = this.contextArticleId;
    }
    if (this.docsSource) {
      req.docsSource = this.docsSource;
    }
    if (this.contextDocSlug) {
      req.contextDocSlug = this.contextDocSlug;
    }
    if (this.portalContext) {
      req.portalContext = this.portalContext; // opaque pass-through; BFF whitelists
    }
    if (opts.providers && opts.providers.length > 0) {
      req.providers = opts.providers;
    }
    if (opts.isRefine && s.lastResolvedParams) {
      req.previousParams = s.lastResolvedParams;
    }
    if (opts.isRefine && s.lastMode) {
      req.previousMode = s.lastMode;
    }
    if (opts.isRefine && s.lastPickedIDs.length > 0) {
      req.previousPickedIds = s.lastPickedIDs;
    }
    if (opts.isRefine) {
      const compact = compactPriorCards(s.turnHistory);
      if (compact.length > 0) req.previousCards = compact;
    }
    // Help/Docsie chip provenance → BFF grounding lane (QA #10). Omitted for
    // typed queries + ordinary chips, so normal routing is unchanged.
    if (opts.helpSource) {
      req.source = 'help';
    }
    return req;
  }

  /** Apply terminal SSE state to the store. Pushes a turn entry, syncs
   *  lastResolvedParams / lastMode / lastPickedIDs from the result, and
   *  clears in-flight flags. */
  private finalizeTurn(query: string, sseData: SSEData): void {
    const resolvedParams = sseData.result?.resolvedParams ?? null;
    const entry: TurnEntry = { query, sseData, resolvedParams };
    // If an optimistic pending turn is on screen (chip echo), swap it in place;
    // otherwise append. Same-position swap keeps the user bubble steady and lets
    // the emma side transition thinking → result.
    //
    // Search the whole history, not just the tail: a synthetic turn can be pushed
    // WHILE an answer is in flight (a doc card from the host's help entry, a
    // mirrored deploy event), which leaves the pending turn no longer last. Only
    // reclaiming a trailing one strands it forever — the user's question renders
    // twice and the orphan is a dead plate with a no-op Stop, surviving every
    // later submit because dropTrailingPendingTurn is also tail-only.
    const h = this.store.state.turnHistory;
    let pendingIndex = -1;
    for (let i = h.length - 1; i >= 0; i--) {
      if (h[i]!.pending) {
        pendingIndex = i;
        break;
      }
    }
    if (pendingIndex >= 0) {
      this.store.replaceTurnAt(pendingIndex, entry);
    } else {
      this.store.pushTurn(entry);
    }

    if (resolvedParams) this.store.setLastResolvedParams(resolvedParams);
    if (sseData.result?.mode) this.store.setLastMode(sseData.result.mode);
    if (sseData.deploy_intent) this.store.setLastMode('deploy_intent');
    if (sseData.pick_result) {
      // Wire mode is `pick_from_results` per ChatMode union; SSE event
      // name is `pick_result` (different naming on the wire vs in state).
      this.store.setLastMode('pick_from_results');
      if (sseData.pick_result.pickedIds) {
        this.store.setLastPickedIDs(sseData.pick_result.pickedIds);
      }
    }
    if (sseData.consultative && !sseData.result) {
      this.store.setLastMode('consultative');
    }
    if (sseData.oob) this.store.setLastMode('oob');

    this.stopThinkingCycle();
    this.store.setSubmitting(false);
  }

  /** Scenario-aware thinking-message cycle. Lifted from monolith
   *  index.html :2772-2777 + :2961-2964. Behaviour 1:1 — show messages[0]
   *  immediately, then advance every 700ms; stop after the last message
   *  (no looping). SSE thinking_progress events override the displayed
   *  text via the onThinkingProgress handler. */
  private startThinkingCycle(text: string): void {
    // STOP any prior cycle FIRST. This also clears thinkingMessage
    // to '' by default — must be done before we set messages[0],
    // otherwise the order would clobber our just-set first message
    // and produce a visible empty-text gap until the first 700ms tick.
    this.stopThinkingCycle();
    const messages = pickThinkingMessages(text);
    this.thinkingMessage = messages[0]!;
    let i = 0;
    this.thinkingCycleTimer = window.setInterval(() => {
      i++;
      if (i < messages.length) {
        this.thinkingMessage = messages[i]!;
      } else {
        // Basket exhausted — stop cycling. SSE messages take over from
        // here (or the message stays at messages[messages.length - 1]).
        this.stopThinkingCycle(/* keepText */ true);
      }
    }, 700);
  }

  /** Compose-phase cycle (E). After the server's "Composing…" beat, the BFF is
   *  blocked on one grounding LLM call (~10-20s) with no more SSE — so cycle a
   *  short basket of grounding-flavoured copy (~2.2s each) so the indicator
   *  doesn't read as frozen. NOT a fake server beat — purely local progress
   *  copy. Holds on the last line if grounding outlasts the basket; cleared by
   *  stopThinkingCycle on result/abort. */
  private startComposeCycle(): void {
    this.stopThinkingCycle();
    this.thinkingMessage = COMPOSE_MESSAGES[0]!;
    let i = 0;
    this.composeCycleTimer = window.setInterval(() => {
      i++;
      if (i < COMPOSE_MESSAGES.length) {
        this.thinkingMessage = COMPOSE_MESSAGES[i]!;
      } else if (this.composeCycleTimer !== null) {
        // Basket exhausted — hold the last line; stop ticking.
        window.clearInterval(this.composeCycleTimer);
        this.composeCycleTimer = null;
      }
    }, 2200);
  }

  private stopThinkingCycle(keepText = false): void {
    if (this.thinkingCycleTimer !== null) {
      window.clearInterval(this.thinkingCycleTimer);
      this.thinkingCycleTimer = null;
    }
    if (this.composeCycleTimer !== null) {
      window.clearInterval(this.composeCycleTimer);
      this.composeCycleTimer = null;
    }
    if (!keepText) this.thinkingMessage = '';
  }

  override render() {
    const submitting = this.store.state.isSubmitting;
    const hasTurns = this.store.state.turnHistory.length > 0;
    // State machine mirrors monolith timing:
    //   • turnHistory empty: <emma-chat-input> visible (with .is-thinking
    //     gradient + cycling messages overlay during submit). The input
    //     itself owns its prompt-card surface; chat-frame is NOT applied
    //     here to avoid a double-card visual.
    //   • turnHistory non-empty: input is gone, message-list owns the
    //     conversation. Refine input lives inside the last turn's
    //     bubble (T09). The synthetic scanning turn (added in
    //     emma-chat-message-list) handles refine-flow scanning state.
    // Carry-deploy is fetching the single ResultCard before the first
    // turn lands. Render a minimal loading view — without this branch
    // the user sees the demo typewriter from emma-chat-input, which
    // implies "you can type a query" while we're actually loading their
    // landing pick. The loading view sits on the same chat-frame
    // surface as the eventual result bubble for visual continuity.
    if (this.carryDeployLoading && !hasTurns) {
      return html`
        <div class="chat-frame">
          <div class="carry-loading">
            <div class="carry-loading__dots">
              <span></span><span></span><span></span>
            </div>
            <div class="carry-loading__text">Loading your selection…</div>
          </div>
        </div>
      `;
    }
    return hasTurns
      ? html`
          <div
            class="chat-frame${this.resetting ? ' is-resetting' : ''}"
            @emma-turn-revealed=${this.handleTurnRevealed}
            @emma-clear-conversation=${this.handleClearConversation}
            @emma-stop-thinking=${this.handleStopThinking}
            @emma-carry-refine-here=${this.handleCarryRefineHere}
            @emma-system-event-action=${this.handleSystemEventAction}
          >
            <emma-chat-message-list
              thinking-message=${this.thinkingMessage}
              max-refines=${this.maxRefines}
              api-base=${this.apiBase}
              surface=${this.surface}
              ?external-deploy-handler=${this.externalDeployHandler}
              @emma-refine-submit=${this.handleRefineSubmit}
              @emma-chip-click=${this.handleChipClick}
              @emma-card-click=${this.handleCardClick}
              @emma-deploy-click=${this.handleDeployClick}
            ></emma-chat-message-list>
          </div>
        `
      : html`
          <emma-chat-input
            .providers=${this.providers}
            api-base=${this.apiBase}
            ?disabled=${submitting}
            ?thinking=${submitting}
            thinking-message=${this.thinkingMessage}
            @emma-input-submit=${this.handleInputSubmit}
            @emma-stop-thinking=${this.handleStopThinking}
          ></emma-chat-input>
        `;
  }

  // ============================================================
  //                       Public API
  // ============================================================
  // The host page (portal SPA) interacts with the widget by:
  //   - setting attributes / properties (api-base, auth-token, etc.)
  //   - listening for composed:true CustomEvents (emma-deploy-click,
  //     emma-card-click, emma-chip-click, emma-input-submit,
  //     emma-refine-submit, emma-clear-conversation, emma-stop-thinking)
  //   - calling the imperative methods below for conveniences that
  //     don't fit the attribute model (token rotation, programmatic
  //     reset, programmatic submit).
  // All methods are no-ops when called against a not-yet-connected
  // widget — guards keep the portal's call sites from having to
  // worry about lifecycle.

  /** Rotate the auth token without re-creating the widget. Use this
   *  after a refresh-token call on the portal side — the change
   *  flows into all three service clients (SSE / API / Capabilities)
   *  via Lit's `updated()` lifecycle, so the next request uses the
   *  new token. In-flight requests keep using the prior token until
   *  they resolve (intentional — aborting mid-stream would surface
   *  as a UX error). Pass empty string to revert to anonymous mode. */
  setAuthToken(token: string): void {
    this.authToken = token;
  }

  /** Update the session-id at runtime. Useful when the portal wants
   *  to bind the conversation to a specific user/account context
   *  (e.g. after switching workspaces). The new id takes effect on
   *  the next request; in-flight requests keep their original id. */
  setSessionId(id: string): void {
    this.sessionId = id;
  }

  /** Reset the conversation to the initial input screen, same as the
   *  inline "Start over" chip in OOB suggestions. No-op when the
   *  conversation is already empty. Plays the 200ms fade-out + input
   *  arrival animation; the promise (if you want one later) would
   *  resolve after that window. */
  resetConversation(): void {
    this.clearConversation();
  }

  /** Programmatically submit a query as if the user typed it into the
   *  input. Used by portal flows that route the user into the widget
   *  with a pre-filled prompt (e.g. "compare H100 across providers"
   *  from a CTA elsewhere in the app). Treated as initial search if
   *  no turns yet, else as refine. */
  submitQuery(text: string): void {
    const trimmed = text.trim();
    if (!trimmed) return;
    void this.runSearch(trimmed, {
      isRefine: this.store.state.turnHistory.length > 0,
    });
  }

  /** Re-hydrate a verbatim conversation transcript carried from the standalone
   *  chat into this (portal-embedded) widget — dialog-carry Phase 1. The portal
   *  fetches the stashed turns (POST /api/hero/transcript/resume) and passes
   *  them here. Pushes them into turnHistory so the user sees their prior
   *  conversation, then restores refine context (last*) so a follow-up refine
   *  continues the thread instead of starting fresh.
   *
   *   - Idempotent: no-op if a conversation already exists (the portal's
   *     maybeAutoOpen can fire more than once per landing).
   *   - Hydrated turns are marked (TurnEntry.hydrated) so the turn component
   *     suppresses the reveal-animation theatre — they're already-answered,
   *     replayed verbatim, not live searches.
   *   - Call this BEFORE any config-card seed (startWithConfig) so the picked
   *     config lands as the final turn.
   *
   *  When `selectedConfigId` is supplied (the config the user carried from the
   *  deploy click), the matching card is marked selected IN PLACE — so the
   *  restored conversation shows the pick highlighted in its own turn, and the
   *  portal can SKIP a redundant re-fetch of that same config. Returns `true`
   *  when the config was found+selected in the carried turns, `false` otherwise
   *  (nothing to hydrate, already hydrated, or the config isn't in the
   *  transcript → the portal falls back to startWithConfig). */
  hydrateTranscript(turns: TurnEntry[], selectedConfigId?: string): boolean {
    if (!Array.isArray(turns) || turns.length === 0) return false;
    if (this.store.state.turnHistory.length > 0) return false; // idempotent

    for (const t of turns) {
      if (!t || typeof t !== 'object') continue;
      // Re-bound systemEvent fields: the transcript blob is attacker-influencable
      // (anonymous stash endpoint + nonce in the landing URL), so action names /
      // labels must pass the SAME gate as live pushEvent input — otherwise a
      // crafted transcript could render a button dispatching an arbitrary or
      // reserved event name (crit-review F2).
      const entry: TurnEntry = { ...t, hydrated: true, pending: false };
      if (entry.systemEvent) entry.systemEvent = boundSystemEvent(entry.systemEvent);
      // Same reasoning as systemEvent: a doc-card in a carried transcript is
      // attacker-influencable input, and its `url` becomes an <a href> + the
      // clipboard payload. Re-bound it at intake; an unbounded/garbage card
      // degrades to a plain (empty) turn rather than rendering a hostile link.
      if (entry.docCard) {
        const rebound = boundDocCard(entry.docCard);
        // A card that fails the gate leaves NOTHING to render: query is '' and
        // sseData is {}, which the turn view shows as a permanent typing plate.
        // Drop the turn instead of restoring a brick.
        if (!rebound) continue;
        entry.docCard = rebound;
      }
      this.store.pushTurn(entry);
    }

    // Restore last* exactly as finalizeTurn does, replayed per turn IN ORDER so
    // the final refine state matches a live session: the last result-bearing
    // turn's resolvedParams persists, the last turn's mode wins.
    for (const t of turns) {
      const sse = t?.sseData;
      if (!sse) continue;
      const resolved = sse.result?.resolvedParams ?? null;
      if (resolved) this.store.setLastResolvedParams(resolved);
      if (sse.result?.mode) this.store.setLastMode(sse.result.mode);
      if (sse.deploy_intent) this.store.setLastMode('deploy_intent');
      if (sse.pick_result) {
        this.store.setLastMode('pick_from_results');
        if (sse.pick_result.pickedIds) {
          this.store.setLastPickedIDs(sse.pick_result.pickedIds);
        }
      }
      if (sse.consultative && !sse.result) this.store.setLastMode('consultative');
      if (sse.oob) this.store.setLastMode('oob');
    }

    return selectedConfigId ? this.selectCarriedConfig(selectedConfigId) : false;
  }

  /** Highlight the carried deploy config in the just-hydrated turns: find the
   *  card whose `configId` matches and mark it selected (`tXcY`). The turn that
   *  owns it is either already the expanded last turn, or force-expanded by the
   *  message-list. Returns true when a card was found+selected. */
  private selectCarriedConfig(configId: string): boolean {
    const id = findSelectedCardId(this.store.state.turnHistory, configId, this.surface);
    if (!id) return false;
    this.store.setSelectedCardId(id);
    return true;
  }

  /** Inject a display-only system/status bubble into the conversation. The
   *  portal uses this to mirror deploy-wizard toasts (their TEXT) into the chat
   *  — e.g. "Pre-filled from your landing pick — Hetzner · hetzner-fsn1 · 2 vCPU"
   *  — so the deploy context the user kicked off FROM the chat lives in the
   *  thread. Rendered with the same emma bubble as a normal answer (no bespoke
   *  toast styling).
   *
   *  Display-only by design: the entry carries NO sseData and never touches
   *  lastResolvedParams, so it can't affect refine/grounding, and it is NEVER
   *  sent to the BFF — the search request body is only query + portalContext +
   *  previousParams, never message history. That makes it safe even if the text
   *  names a resource (it stays in the user's own browser, shown back to them).
   *
   *  Optional `action` {label, event} adds a link-style button under the text;
   *  clicking it dispatches a bubbling composed CustomEvent named `event` from
   *  this host element (same contract as emma-deploy-click), letting the host
   *  wire the actual behavior — e.g. "Open a support ticket" → the portal opens
   *  its support drawer (QA-2 #22: offer the drawer, never auto-open it). The
   *  action is bounded by boundSystemEventAction: label ≤60, event in the
   *  widget out-event namespace (emma-[a-z0-9-]+) and NOT a reserved in-band /
   *  contract name (a systemEvent can't spoof emma-deploy-click etc.). A
   *  malformed action is dropped, the text still shows. The click is RELAYED:
   *  the turn dispatches a private emma-system-event-action carrying the name
   *  in detail, and handleSystemEventAction re-dispatches from THIS host — the
   *  action event never travels through the widget's internal listeners.
   *
   *  Empty text is a no-op; text is trimmed and capped. */
  pushEvent(text: string, action?: { label?: unknown; event?: unknown }): void {
    const trimmed = (text ?? '').toString().trim();
    if (!trimmed) return;
    this.store.pushTurn({
      query: '',
      sseData: {},
      resolvedParams: null,
      isSyntheticSystem: true,
      systemEvent: {
        text: trimmed.slice(0, SYSTEM_EVENT_TEXT_MAX),
        action: boundSystemEventAction(action),
      },
    });
  }

  /** Monotonic id for doc-card turns. Used to find the right turn again when
   *  the async excerpt lands (the user may have asked something meanwhile), so
   *  the patch never lands on the wrong turn. Not random — deterministic ids
   *  keep tests and log correlation simple. */
  private docCardSeq = 0;

  /** Drop a documentation card into the transcript — the portal's Help entry.
   *
   *  The host resolves the article for the current screen from the docs
   *  manifest and hands it over; the widget renders title + excerpt +
   *  [Open in docs ↗] / [Copy link] and a row of follow-up chips. Clicking a
   *  chip runs the normal help lane (origin='help' → BFF grounding/RAG), which
   *  is the ONLY way this flow talks to a model.
   *
   *  ZERO LLM CALLS ON OPEN — this is a hard requirement, not an optimization
   *  (docs-import-plan.md arbitration #2: latency, token cost, and the risk of
   *  a model paraphrasing instruction steps wrong). Opening the card issues at
   *  most ONE request, the optional GET /api/hero/docs/content excerpt lookup,
   *  which touches no model. Pass `fetchExcerpt: false` to suppress even that.
   *
   *  Degradation: the card renders entirely from the argument. A dead/absent
   *  BFF only means the body stays the manifest `description`.
   *
   *  Argument (all strings trimmed and capped at intake — see boundDocCard):
   *    slug         manifest slug ('' = docs home). REQUIRED (may be empty
   *                 string); must match /^[a-z0-9][a-z0-9/_-]{0,120}$/ — the
   *                 same shape the BFF allowlists.
   *    title        REQUIRED, non-empty. Card headline.
   *    url          REQUIRED absolute http(s) URL of the article. Relative
   *                 URLs are REJECTED (the docs origin is the host's to
   *                 resolve; the widget must not guess it from its own page).
   *    description  optional manifest blurb — the offline-safe body text.
   *    chips        optional 1-4 {label, prompt} follow-ups. Omitted → the
   *                 widget derives 3 deterministic ones from the title
   *                 (doc-card-chips.ts), still with no model call.
   *    fetchExcerpt optional, default true. false = render the description
   *                 only and make no network call at all.
   *
   *  Returns true when a card was appended. Returns false — a no-op — when the
   *  payload fails the bound (missing title / non-http url / malformed slug),
   *  or when the LAST turn is already this same slug's card, so a Help entry
   *  that fires twice (store-intent queue + mount) doesn't stack duplicates.
   *
   *  Works before the first turn (the input screen swaps to the transcript)
   *  and mid-conversation (the card appends as the newest turn). */
  showDocCard(doc: {
    slug: string;
    title: string;
    url: string;
    description?: string;
    chips?: { label: string; prompt: string }[];
    fetchExcerpt?: boolean;
  }): boolean {
    const bound = boundDocCard(doc);
    if (!bound) {
      // The likeliest integration mistake is a relative url (every manifest url
      // is relative — the host must absolutize). Silence would look like the
      // feature simply not working.
      console.warn('[emma-chat] showDocCard rejected the payload (needs title + absolute http(s) url)', doc);
      return false;
    }

    // Dedupe against the last DOC-CARD turn, not the last turn: the host's intent
    // queue can fire twice, and if anything at all landed in between (a pending
    // turn from an in-flight answer) a tail-only check would let the duplicate
    // through.
    const history = this.store.state.turnHistory;
    let lastCard: DocCardEntry | undefined;
    for (let i = history.length - 1; i >= 0; i--) {
      if (history[i]!.docCard) {
        lastCard = history[i]!.docCard;
        break;
      }
    }
    if (lastCard && lastCard.slug === bound.slug) return false;

    const token = `dc${++this.docCardSeq}`;
    const card: DocCardEntry = { ...bound, token };
    this.store.pushTurn({
      query: '',
      sseData: {},
      resolvedParams: null,
      isSyntheticSystem: true,
      docCard: card,
    });

    if (doc.fetchExcerpt !== false) void this.fetchDocExcerpt(token, card.slug);
    return true;
  }

  /** Progressive excerpt upgrade for a just-pushed doc-card. Fire-and-forget:
   *  every failure (BFF off, 404, rate-limit, CORS) leaves the card exactly as
   *  the host rendered it. Patches the turn found by token, NOT the last turn —
   *  the user may have asked a follow-up while this was in flight. */
  private async fetchDocExcerpt(token: string, slug: string): Promise<void> {
    let excerpt = '';
    try {
      const res = await this.apiClient.getDocExcerpt(slug);
      excerpt = (res?.excerpt ?? '').toString().trim().slice(0, DOC_CARD_TEXT_MAX);
    } catch {
      return;
    }
    if (!excerpt) return;
    const turns = this.store.state.turnHistory;
    const idx = turns.findIndex((t) => t.docCard?.token === token);
    if (idx < 0) return; // conversation was reset meanwhile
    const turn = turns[idx]!;
    this.store.replaceTurnAt(idx, {
      ...turn,
      docCard: { ...turn.docCard!, excerpt },
    });
  }

  /** Relay for a clicked systemEvent action (see pushEvent). The event NAME
   *  arrives in detail (already bounded at intake by boundSystemEventAction /
   *  boundSystemEvent); re-dispatching from the HOST means the CustomEvent
   *  starts in the light DOM — host-page listeners on <emma-chat> receive it,
   *  the widget's own shadow-tree handlers never can. Belt-and-braces: the
   *  bound is re-checked here so a turn entry that somehow skipped intake
   *  still can't dispatch a reserved or garbage name. */
  private handleSystemEventAction(e: CustomEvent<{ event?: string }>): void {
    e.stopPropagation();
    const bounded = boundSystemEventAction({ label: 'x', event: e.detail?.event });
    if (!bounded) return;
    this.dispatchEvent(new CustomEvent(bounded.event, { bubbles: true, composed: true }));
  }

  /** Programmatically fill the input WITHOUT submitting — user reviews
   *  the prompt then clicks Send. Bridge for host-page preset chips:
   *
   *    document.querySelectorAll('.chip-preset').forEach((btn) =>
   *      btn.addEventListener('click', () =>
   *        widget.setInputText(btn.dataset.prompt ?? ''),
   *      ),
   *    );
   *
   *  When the conversation has already started (`turnHistory.length > 0`
   *  → input is unmounted, message-list visible), the call resets the
   *  conversation first and waits for the input to re-mount before
   *  setting the text. Reset uses the same 200 ms fade-out as the OOB
   *  Start over chip. */
  setInputText(text: string): void {
    const apply = (): void => {
      const input = this.renderRoot?.querySelector('emma-chat-input');
      // Cast to any to call setText without re-importing the type here —
      // method is part of the public API contract on EmmaChatInput.
      (input as { setText?: (t: string) => void } | null)?.setText?.(text);
    };
    if (this.store.state.turnHistory.length === 0) {
      apply();
      return;
    }
    // Conversation active: reset first, then fill once input has
    // re-mounted. 200 ms fade + ~20 ms render slack = 220 ms.
    this.clearConversation();
    window.setTimeout(apply, 220);
  }

  /** Carry-deploy entry for VM / Spot: portal HeroDeployPage uses this
   *  when the user arrives from landing with `?workload=vm&config=…&dataCenterId=…`.
   *  The widget fetches the single ResultCard from /api/hero/config and
   *  injects it as a synthetic system-only turn (no user-typed query —
   *  rendered without a user bubble; see TurnEntry.isSyntheticSystem).
   *  Subsequent user refines / deploy-clicks work as usual.
   *
   *  On 404 (config no longer in emma SDK catalog — e.g. emma deprecated
   *  the SKU between landing-click and signup) the widget injects an
   *  oob turn with copy "This configuration is no longer available." +
   *  the standard refine input, terminating carry-deploy mode.
   *
   *  Other errors (transport / 5xx / 501): rejected promise with the
   *  underlying NetworkError so the host can surface it. */
  async startWithConfig(
    configId: string,
    opts: {
      workload: 'vm' | 'spot' | 'kubernetes';
      dataCenterId?: string;
      location?: string;
      provider?: string;
      acceleratorId?: string;
      k8sMode?: string;
      workerCount?: number;
      nodeMin?: number;
      nodeMax?: number;
      utmSource?: string;
    },
  ): Promise<void> {
    const wl = opts.workload;
    if (wl !== 'vm' && wl !== 'spot' && wl !== 'kubernetes') {
      throw new Error(`startWithConfig: workload must be 'vm', 'spot' or 'kubernetes' (got '${String(wl)}')`);
    }
    if (!configId) {
      throw new Error('startWithConfig: configId is required');
    }
    // Narrow key differs by workload (mirrors the BFF /api/hero/config
    // contract): K8s scopes by numeric location, vm/spot by dataCenterId.
    if (wl === 'kubernetes') {
      if (!opts.location) {
        throw new Error('startWithConfig: location is required for kubernetes');
      }
    } else if (!opts.dataCenterId) {
      throw new Error('startWithConfig: dataCenterId is required for vm/spot');
    }
    this.store.setCarryDeployMode('config');
    this.carryDeployLoading = true;
    try {
      const card = await this.apiClient.getConfig(wl, configId, {
        dataCenterId: opts.dataCenterId,
        location: opts.location,
        acceleratorId: opts.acceleratorId,
        k8sMode: opts.k8sMode,
        workerCount: opts.workerCount,
        nodeMin: opts.nodeMin,
        nodeMax: opts.nodeMax,
      });
      const sseData: SSEData = {
        result: {
          summary: '',
          workloadType: opts.workload,
          results: [card],
        },
      };
      const entry: TurnEntry = {
        query: '',
        sseData,
        resolvedParams: null,
        isSyntheticSystem: true,
      };
      this.store.pushTurn(entry);
      this.carryDeployLoading = false;
    } catch (err) {
      this.carryDeployLoading = false;
      // 404 → oob fallback. Other errors propagate so the host page sees them.
      if (err && (err as { name?: string }).name === 'NotFoundError') {
        const sseData: SSEData = {
          oob: {
            message: 'This configuration is no longer available. Search for something similar below.',
            suggestions: [],
          },
        };
        this.store.pushTurn({
          query: '',
          sseData,
          resolvedParams: null,
          isSyntheticSystem: true,
        });
        this.store.setCarryDeployMode(null);
        return;
      }
      this.store.setCarryDeployMode(null);
      throw err;
    }
  }

  /** Carry-deploy entry for Kubernetes: portal HeroDeployPage uses this
   *  when the user arrives from landing with `?workload=kubernetes`
   *  (no config — K8s navigate-mode never carries a SKU UUID, the
   *  worker-node configuration happens inside the wizard). The widget
   *  injects a synthetic prompt bubble with two inline CTAs:
   *
   *    "Continue to wizard" → dispatches emma-navigate-intent
   *    "Refine here"        → focuses refine input (standard chat)
   *
   *  See <emma-chat-turn>.renderCarryNavigatePrompt for the rendering. */
  startWithNavigation(opts: { workload: 'kubernetes'; utmSource?: string }): void {
    if (opts.workload !== 'kubernetes') {
      throw new Error(`startWithNavigation: only workload='kubernetes' supported in Phase 1`);
    }
    this.store.setCarryDeployMode('navigate');
    this.store.pushTurn({
      query: '',
      sseData: {},
      resolvedParams: null,
      isSyntheticSystem: true,
      carryNavigatePrompt: {
        workload: opts.workload,
        utmSource: opts.utmSource,
      },
    });
  }
}

/** Compose-phase progress copy (E). Cycled locally (~2.2s each) after the
 *  server's "Composing…" beat to keep the indicator alive through the single
 *  grounding LLM call. Order narrates the grounding pipeline. */
const COMPOSE_MESSAGES = [
  'Reading the matched articles…',
  'Cross-checking the sources…',
  'Grounding the steps in the docs…',
  'Writing it up…',
];

/** True for the BFF grounding "Composing a grounded answer…" beat — the cue to
 *  hand off to the local compose cycle. Substring-matched so a copy tweak on
 *  the BFF side doesn't silently break the handoff. */
function isComposingMessage(msg: string): boolean {
  return /composing/i.test(msg);
}

/** Pick a thinking-message basket from the query text. Mirrors monolith
 *  getScenarioKey + thinkingMessages (index.html :2764-2777). */
function pickThinkingMessages(text: string): string[] {
  const t = text.toLowerCase();
  if (t.includes('gpu') || t.includes('training') || t.includes('a100')) {
    return ['Scanning GPU availability…', 'Configuring cluster…', 'Allocating resources…'];
  }
  if (t.includes('k8s') || t.includes('kubernetes') || t.includes('autoscale')) {
    return ['Checking K8s configurations…', 'Analyzing node requirements…', 'Setting up autoscale…'];
  }
  if (t.includes('spot') || t.includes('inference')) {
    return ['Scanning spot markets…', 'Comparing spot pricing…', 'Evaluating availability…'];
  }
  return ['Searching providers…', 'Comparing pricing…', 'Finding best deal…'];
}

/** Compact prior turn cards for the next refine request. Mirrors monolith
 *  audit §5.1 — only the fields the BFF reads back. */
function compactPriorCards(turns: TurnEntry[]): CompactCard[] {
  const out: CompactCard[] = [];
  turns.forEach((t, ti) => {
    const cards = t.sseData.result?.results ?? [];
    cards.forEach((c, ci) => {
      out.push({
        id: `t${ti}c${ci}`,
        provider: c.provider,
        location: c.locationDisplay,
        continent: c.continent,
        gpu: c.gpu,
        vcpu: c.vcpu,
        ram: c.ram,
        pricePerHour: c.pricePerHour,
        pricePerMonth: c.pricePerMonth,
        isSpot: c.isSpot,
        volumeGb: c.volumeGb,
        volumeType: c.volumeType,
      });
    });
  });
  return out;
}

/** Generate a session-id for cross-request correlation when the host
 *  page didn't provide one. Prefers `crypto.randomUUID` (modern browsers,
 *  cryptographically random); falls back to a Date.now + Math.random
 *  composite that's unique enough for analytics grouping but not for
 *  security. Format isn't checked by the BFF — it just echoes/logs it. */
function generateSessionId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 10);
  return `s-${ts}-${rand}`;
}

declare global {
  interface HTMLElementTagNameMap {
    'emma-chat': EmmaChat;
  }
}
